import _sortBy from 'lodash/sortBy';
import _without from 'lodash/without';
import { isSameDay, parseISO, isPast } from 'date-fns';
import { DIVISIONS_LIST, GROUPS_LIST, DEPARTMENTS_LIST } from 'constants/index';
import { priceFormatter, getUSFormatDate, convertKebabCaseDateString } from 'utils';

export const getDepartmentNameById = (id) => DIVISIONS_LIST[id] || 'Department not found';

export const getSalesItemName = (id, masterDivisionList = {}) => {
  const { divisions = {}, groups = {}, departments = {} } = masterDivisionList;
  const name =
    divisions[id] || DIVISIONS_LIST[id] || groups[id] || GROUPS_LIST[id] || departments[id] || DEPARTMENTS_LIST[id];
  const number = divisions[id] || DIVISIONS_LIST[id] ? '' : `${id} `;
  return name ? `${number}${name}` : null;
};

export const getSalesItemNamePerType = (type, id, masterDivisionList = {}) => {
  const { divisions = {}, groups = {}, departments = {} } = masterDivisionList;

  let name;

  switch (type) {
    case 'division':
    case 'division-by-designer':
      name = divisions[id] || DIVISIONS_LIST[id];
      break;

    case 'group':
      name = groups[id] || GROUPS_LIST[id];
      break;

    case 'department':
      name = departments[id] || DEPARTMENTS_LIST[id];
      break;

    default:
      name = null;
  }

  const number = divisions[id] || DIVISIONS_LIST[id] ? '' : `${id} `;
  return name ? `${number}${name}` : null;
};

export const getGroupAndDivisionIdsFromExpandedNodes = (expandedNodes, masterDivisionList = {}) => {
  const { divisions = {}, groups = {} } = masterDivisionList;
  const currentNodeId = expandedNodes[0];
  const groupId = (groups[currentNodeId] && currentNodeId) || (GROUPS_LIST[currentNodeId] && currentNodeId);
  const divisionId = expandedNodes.find((nodeId) => (divisions && divisions[nodeId]) || DIVISIONS_LIST[nodeId]);

  return {
    groupId,
    divisionId,
  };
};

export const mapDivisionCodeToDepartmentName = (divisionList = [], masterDivisionList = {}) =>
  divisionList.map((item) => ({
    ...item,
    value: (masterDivisionList && masterDivisionList[item.key]) || DIVISIONS_LIST[item.key],
  }));

export const adjustPerformanceParams = (options) => {
  const params = {};
  return Object.entries(options).reduce((option, [key, value]) => {
    switch (key) {
      case 'filterType':
        params.filterType = value;
        break;
      case 'fromDate':
        params.dateFrom = value;
        break;
      case 'toDate':
        params.dateTo = value;
        break;
      case 'filters':
        params.filter = { ...value };
        break;
      case 'pagination':
        params.pagination = { ...value };
        break;
      case 'searchText':
        params.searchText = value;
        break;
      default:
        break;
    }
    return params;
  }, {});
};

export const refineRemindersBySectionsAndSort = (reminders) => {
  const sortedReminders = _sortBy(reminders, [
    ({ reminderIsoDate }) => parseISO(reminderIsoDate),
    ({ createdIsoDateTime }) => parseISO(createdIsoDateTime),
  ]);

  const completedReminders = sortedReminders.filter(({ completed }) => completed);
  const todayAndPastReminders = sortedReminders.filter(({ reminderIsoDate }) => {
    const isReminderDatePast = isPast(parseISO(reminderIsoDate));
    const isReminderDateToday = isSameDay(Date.now(), parseISO(reminderIsoDate));

    return isReminderDatePast || isReminderDateToday;
  });

  const todayUncompletedReminders = _without(todayAndPastReminders, ...completedReminders);
  const upcomingReminders = _without(sortedReminders, ...completedReminders, ...todayUncompletedReminders);

  return {
    todayUncompletedReminders,
    upcomingReminders,
    completedReminders,
  };
};

export const mpsFormatter = (data, style) => {
  switch (style) {
    case 'netSales':
    case 'averageTrip':
      return priceFormatter(data);
    case 'clientConnect':
    case 'newClientConversions':
      return data ? `${data}%` : '0%';
    default:
      return data;
  }
};

export const formatSalesDetailsWidgetData = (scorecardData, validThrough = '') => {
  const { wardrobing, clientConnect, averageTrip, averageTripPerCustomer } = scorecardData || {};

  return {
    wardrobing: {
      title: 'Departments',
      header: 'Wardrobing',
      scorecardCategory: 'wardrobing',
      subMetrics: {
        title: 'OHB Total',
        subTitle: 'OHB % of Net',
        primary: wardrobing?.sales,
        secondary: wardrobing?.netSales,
      },
      ...wardrobing,
    },
    clientConnect: {
      title: 'Client Connects',
      header: 'Conversion Rates',
      scorecardCategory: 'clientConnect',
      ...clientConnect,
    },
    averageTrip: {
      title: 'Average Trip Value',
      header: 'Customer Trips',
      scorecardCategory: 'averageTripValue',
      secondaryMetrics: {
        title: 'Average Trips Per Customer',
        scorecardCategory: 'averageTrips',
        ...averageTripPerCustomer,
      },
      ...averageTrip,
      salesThrough: validThrough && `Updated ${validThrough}`,
    },
  };
};
export const formatNetSalesWidgetData = (netSalesData, displayReturns = false, lastYearSales) => {
  const { salesThrough, returns, returnRate } = netSalesData || {};

  return {
    scorecardCategory: 'netSales',
    lastYearSales,
    subMetrics: displayReturns && {
      title: 'Returns',
      subTitle: 'Return Rate',
      primary: returns,
      secondary: returnRate,
    },
    ...netSalesData,
    salesThrough: salesThrough && `Sales Through ${getUSFormatDate(convertKebabCaseDateString(salesThrough))}`,
  };
};
